import React, { useRef, useState, useCallback, useEffect } from "react";
import LightGallery from "lightgallery/react";
import "lightgallery/css/lightgallery.css";
import "lightgallery/css/lg-thumbnail.css";
import "lightgallery/css/lg-zoom.css";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";

const CarDetailSlider = ({ data, image }) => {
  const lightGalleryRef = useRef(null);
  const thumbStripRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  /* ── Build image list ── */
  const mainImageSrc = image
    ? `${window.CARLOCA_UPLOADS}/cars/${image}`
    : null;

  const galleryImages =
    data
      ?.map((s) =>
        s.image?.startsWith("http")
          ? s.image
          : `${window.CARLOCA_UPLOADS}/cars-gallery/${s.image}`
      )
      .filter((url) => {
        if (!url || !mainImageSrc) return true;
        return url.split("/").pop() !== mainImageSrc.split("/").pop();
      }) || [];

  const finalImages = [mainImageSrc, ...galleryImages].filter(Boolean);
  if (finalImages.length === 0)
    finalImages.push(`${window.CARLOCA_UPLOADS}/dummy.png`);

  /* ── Navigation ── */
  const go = useCallback(
    (dir, e) => {
      e && e.stopPropagation();
      setActiveIndex((p) =>
        dir === "prev"
          ? p === 0
            ? finalImages.length - 1
            : p - 1
          : p === finalImages.length - 1
          ? 0
          : p + 1
      );
    },
    [finalImages.length]
  );

  /* Keyboard nav */
  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowLeft") go("prev");
      if (e.key === "ArrowRight") go("next");
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [go]);

  /* Auto-scroll thumb into view */
  useEffect(() => {
    const strip = thumbStripRef.current;
    if (!strip) return;
    const thumb = strip.children[activeIndex];
    if (thumb)
      thumb.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
  }, [activeIndex]);

  /* Image error fallback */
  const onErr = (e, src) => {
    if (src?.includes("cars-gallery/") && !src?.includes("cars-gallery/carbig/")) {
      e.target.src =
        src.split("cars-gallery/")[0] +
        "cars-gallery/carbig/" +
        src.split("cars-gallery/")[1];
    }
  };

  const openGallery = (i) => lightGalleryRef.current?.openGallery(i);

  return (
    <div className="cds-wrap">
      <style>{`
        /* ─────────── CarDetailSlider ─────────── */
        .cds-wrap { width:100%; overflow:hidden; border-radius:12px; background:#0d1117; box-shadow:0 6px 28px rgba(0,0,0,.28); }
        .cds-wrap * { box-sizing:border-box; }

        /* Stage: always 4:3 */
        .cds-stage {
          position: relative;
          width: 100%;
          padding-top: 75%;   /* 4:3 = 3/4 = 75% */
          overflow: hidden;
          background: #0d1117;
          cursor: zoom-in;
        }

        /* Everything inside the stage floats on top of the padding trick */
        .cds-inner {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        /* Blurred fill background */
        .cds-blur {
          position: absolute;
          inset: -24px;
          background-size: cover;
          background-position: center;
          filter: blur(26px) brightness(.38) saturate(1.5);
          transition: background-image .3s;
          z-index: 0;
        }

        /* Main photo — fills the stage with cover, no void */
        .cds-photo {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          z-index: 1;
          display: block;
          transition: opacity .25s ease;
        }

        /* Bottom gradient for readability of buttons */
        .cds-grad {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 90px;
          background: linear-gradient(to top, rgba(0,0,0,.75) 0%, transparent 100%);
          z-index: 2;
          pointer-events: none;
        }

        /* Prev / Next */
        .cds-btn {
          position: absolute;
          top: 50%; transform: translateY(-50%);
          z-index: 4;
          width: 44px; height: 44px;
          border-radius: 50%;
          background: rgba(255,255,255,.15);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1.5px solid rgba(255,255,255,.3);
          color: #fff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          font-size: 15px;
          transition: background .2s, border-color .2s, transform .2s;
          padding: 0;
          line-height: 1;
        }
        .cds-btn:hover {
          background: #00889c;
          border-color: #00889c;
          transform: translateY(-50%) scale(1.08);
        }
        .cds-btn-prev { left: 12px; }
        .cds-btn-next { right: 12px; }

        /* Pill badges */
        .cds-pill {
          position: absolute;
          bottom: 12px;
          z-index: 4;
          background: rgba(0,0,0,.6);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          color: #fff;
          border: 1px solid rgba(255,255,255,.18);
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          padding: 5px 13px;
          display: flex; align-items: center; gap: 6px;
          cursor: pointer;
          transition: background .2s;
          line-height: 1;
          letter-spacing: .3px;
        }
        .cds-pill:hover { background: #00889c; border-color: #00889c; }
        .cds-pill-left  { left: 12px; cursor: default; pointer-events: none; }
        .cds-pill-right { right: 12px; }

        /* ── Thumbnail strip ── */
        .cds-thumbs {
          display: flex;
          gap: 5px;
          padding: 8px;
          overflow-x: auto;
          background: #141922;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .cds-thumbs::-webkit-scrollbar { display: none; }

        .cds-thumb {
          flex-shrink: 0;
          width: 80px; height: 58px;
          border-radius: 7px;
          overflow: hidden;
          cursor: pointer;
          border: 2.5px solid transparent;
          opacity: .5;
          transition: border-color .2s, opacity .2s, transform .2s;
          background: #222;
        }
        .cds-thumb img {
          width: 100%; height: 100%;
          object-fit: cover; display: block;
        }
        .cds-thumb:hover { opacity: .82; transform: scale(1.05); }
        .cds-thumb.cds-thumb-active {
          border-color: #00889c;
          opacity: 1;
          transform: scale(1.06);
        }

        /* ── Responsive ── */
        @media (max-width: 576px) {
          .cds-btn { width: 36px; height: 36px; font-size: 13px; }
          .cds-btn-prev { left: 8px; }
          .cds-btn-next { right: 8px; }
          .cds-pill { font-size: 11px; padding: 4px 10px; bottom: 8px; }
          .cds-pill-left  { left: 8px; }
          .cds-pill-right { right: 8px; }
          .cds-thumb { width: 64px; height: 46px; }
          .cds-thumbs { padding: 6px; gap: 4px; }
        }
        @media (max-width: 380px) {
          .cds-btn { width: 30px; height: 30px; font-size: 11px; }
          .cds-thumb { width: 54px; height: 38px; }
        }
      `}</style>

      {/* ── Stage ── */}
      <div className="cds-stage" onClick={() => openGallery(activeIndex)}>
        <div className="cds-inner">
          {/* Blurred backdrop (shows through transparent parts) */}
          <div
            className="cds-blur"
            style={{ backgroundImage: `url(${finalImages[activeIndex]})` }}
          />

          {/* Main photo — always fills frame with cover */}
          <img
            key={activeIndex}
            src={finalImages[activeIndex]}
            alt={`Vehicle photo ${activeIndex + 1}`}
            className="cds-photo"
            onError={(e) => onErr(e, finalImages[activeIndex])}
          />

          {/* Bottom gradient overlay */}
          <div className="cds-grad" />

          {/* Prev / Next */}
          {finalImages.length > 1 && (
            <>
              <button
                type="button"
                className="cds-btn cds-btn-prev"
                onClick={(e) => go("prev", e)}
                aria-label="Previous photo"
              >
                <i className="fa-solid fa-chevron-left" />
              </button>
              <button
                type="button"
                className="cds-btn cds-btn-next"
                onClick={(e) => go("next", e)}
                aria-label="Next photo"
              >
                <i className="fa-solid fa-chevron-right" />
              </button>
            </>
          )}

          {/* Counter pill — bottom left */}
          <div className="cds-pill cds-pill-left">
            <i className="fa-solid fa-camera" style={{ fontSize: 10 }} />
            {activeIndex + 1} / {finalImages.length}
          </div>

          {/* View all pill — bottom right */}
          <div
            className="cds-pill cds-pill-right"
            onClick={(e) => { e.stopPropagation(); openGallery(activeIndex); }}
          >
            <i className="fa-solid fa-expand" style={{ fontSize: 10 }} />
            View All
          </div>
        </div>
      </div>

      {/* ── Thumbnail strip ── */}
      {finalImages.length > 1 && (
        <div className="cds-thumbs" ref={thumbStripRef}>
          {finalImages.map((src, idx) => (
            <div
              key={idx}
              className={`cds-thumb${activeIndex === idx ? " cds-thumb-active" : ""}`}
              onClick={() => setActiveIndex(idx)}
            >
              <img
                src={src}
                alt={`Thumbnail ${idx + 1}`}
                onError={(e) => onErr(e, src)}
              />
            </div>
          ))}
        </div>
      )}

      {/* ── LightGallery (fullscreen trigger — hidden) ── */}
      <LightGallery
        onInit={(ref) => (lightGalleryRef.current = ref.instance)}
        plugins={[lgThumbnail, lgZoom]}
        speed={400}
        mode="lg-fade"
        dynamic
        dynamicEl={finalImages.map((url) => ({ src: url, thumb: url }))}
        style={{ display: "none" }}
      />
    </div>
  );
};

export default CarDetailSlider;
